import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { EnvironmentProviders, inject, provideAppInitializer, Provider } from '@angular/core';

import { Container } from 'inversify';

import {
    _BaasApm,
    _BaasContextManager,
    _BaasProfielService,
    _BaasStaticConfigurationApiClient,
    _BaasTransportHelper,
    _BarAppInfoManager,
    _BarXdc,
    _OopsDomService,
    BAAS_METADATA_KEY,
    BAAS_TYPES,
    BaasAppState,
    BaasAuditService,
    BaasAuthService,
    BaasBootstrapOptions,
    BaasCommonApiClient,
    BaasConfigurationService,
    BaasI18n,
    BaasLoggingService,
    BaasMeetpuntService,
    BaasMeldingService,
    BaasSecurityService,
    BaasTelemetry,
    BaasToepassingService,
    BarCacheManager,
    BarCacheStore,
    BarCacheStoreType,
    BarGvkManager,
    BarIdpClient,
    BarNotificationManager
} from '@lvflanders/bar';

import { BAAS_COMMON_API_CLIENT } from '../constants/internal-tokens.constants';
import { BAAS_TELEMETRY, BAR_APP_CACHE, BAR_GLOBAL_CACHE, BAR_LOCALHOST, BAR_MELDINGEN_CACHE, BAR_USER_CACHE } from '../constants/tokens.constants';
import { BaasApmInitHook } from '../hooks/baas-apm-init.hook';
import { BarErrorDelegateHandlerHook } from '../hooks/bar-error-delegate-handler.hook';
import { BarCoreInterceptor } from '../interceptors/bar-core.interceptor';
import { _BaasApmService } from '../services/baas-apm.service';
import { _BarCoreStateService, BaasCoreStateService } from '../services/baas-core-state.service';
import { BarStartupService } from '../services/bar-startup.service';
import { provideBaasErrorHandler, provideBaasInitialized } from './baas-lifecycle.providers';
import { BarMeldingenEngine } from './bar-meldingen-engine.provider';
import { _BarRouteLogger } from './bar-route-logger.provider';

/** Creates a factory which makes the bridge between non-angular @lvflanders/bar (InversifyJS) and Angular @lvflanders/bar-ng. */
function createBarContainerBridgeFactory<T>(serviceIdentifier: any, container: Container) {
    const result = () => container.get<T>(serviceIdentifier);
    return result;
}

function createDefaultCacheStoreFactory(container: Container, cacheStoreType: BarCacheStoreType.Application | BarCacheStoreType.Global) {
    const result = () =>
        container.get<BarCacheStore>(BarCacheStore, {
            tag: { key: BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, value: cacheStoreType }
        });
    return result;
}

function userCacheStoreFactory(cacheManager: BarCacheManager, coreState: BaasAppState) {
    // use lambda for user id because it can change over the lifetime of this application
    return cacheManager.getStore({
        storeType: BarCacheStoreType.User,
        userId: coreState.basiscontext.userId
    });
}

function meldingenCacheStoreFactory(cacheManager: BarCacheManager) {
    return cacheManager.getStore({ storeType: BarCacheStoreType.Global, store: 'meldingen' });
}

export function defineAppProviders(container: Container): Array<Provider | EnvironmentProviders> {
    return [
        {
            provide: Container,
            useValue: container
        },
        {
            provide: BaasLoggingService,
            useFactory: createBarContainerBridgeFactory<BaasLoggingService>(BaasLoggingService, container)
        },
        // here we register our interceptor
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BarCoreInterceptor,
            multi: true
        },
        // next registration is the BAR service used at startup
        BarStartupService,
        provideAppInitializer(() => inject(BarStartupService).load()),
        {
            provide: BaasAuthService,
            useFactory: createBarContainerBridgeFactory<BaasAuthService>(BaasAuthService, container)
        },
        {
            provide: BaasAppState,
            useFactory: createBarContainerBridgeFactory<BaasAppState>(BaasAppState, container)
        },
        _BarCoreStateService,
        BaasCoreStateService,
        {
            provide: _BarAppInfoManager,
            useFactory: createBarContainerBridgeFactory<_BarAppInfoManager>(_BarAppInfoManager, container)
        },
        {
            provide: _BaasContextManager,
            useFactory: createBarContainerBridgeFactory<_BaasContextManager>(_BaasContextManager, container)
        },
        {
            provide: BaasAuditService,
            useFactory: createBarContainerBridgeFactory<BaasAuditService>(BaasAuditService, container)
        },
        {
            provide: BaasToepassingService,
            useFactory: createBarContainerBridgeFactory<BaasToepassingService>(BaasToepassingService, container)
        },
        {
            provide: BaasSecurityService,
            useFactory: createBarContainerBridgeFactory<BaasSecurityService>(BaasSecurityService, container)
        },
        {
            provide: _BaasProfielService,
            useFactory: createBarContainerBridgeFactory<_BaasProfielService>(_BaasProfielService, container)
        },
        {
            provide: BaasConfigurationService,
            useFactory: createBarContainerBridgeFactory<BaasConfigurationService>(BaasConfigurationService, container)
        },
        {
            provide: BaasMeetpuntService,
            useFactory: createBarContainerBridgeFactory<BaasMeetpuntService>(BaasMeetpuntService, container)
        },
        {
            provide: _BarXdc,
            useFactory: createBarContainerBridgeFactory<_BarXdc>(_BarXdc, container)
        },
        {
            provide: BaasMeldingService,
            useFactory: createBarContainerBridgeFactory<BaasMeldingService>(BaasMeldingService, container)
        },
        BarMeldingenEngine,
        // NOTE still used by @lvflanders/startscherm-ng-ui
        {
            provide: BarIdpClient,
            useFactory: createBarContainerBridgeFactory<BarIdpClient>(BarIdpClient, container)
        },
        {
            provide: BarCacheManager,
            useFactory: createBarContainerBridgeFactory<BarCacheManager>(BarCacheManager, container)
        },
        {
            provide: BarNotificationManager,
            useFactory: createBarContainerBridgeFactory<BarNotificationManager>(BarNotificationManager, container)
        },
        {
            // inject default cache store which is for the current app
            provide: BarCacheStore,
            useFactory: createDefaultCacheStoreFactory(container, BarCacheStoreType.Application),
            deps: [BarCacheManager]
        },
        {
            // inject current app cache
            provide: BAR_APP_CACHE,
            useFactory: createDefaultCacheStoreFactory(container, BarCacheStoreType.Application),
            deps: [BarCacheManager]
        },
        {
            // inject the user cache
            provide: BAR_USER_CACHE,
            useFactory: userCacheStoreFactory,
            deps: [BarCacheManager, BaasAppState]
        },
        {
            // inject the global cache
            provide: BAR_GLOBAL_CACHE,
            useFactory: createDefaultCacheStoreFactory(container, BarCacheStoreType.Global),
            deps: [BarCacheManager]
        },
        {
            provide: BAR_MELDINGEN_CACHE,
            useFactory: meldingenCacheStoreFactory,
            deps: [BarCacheManager]
        },
        // bridge to link the GVK state to the @lvflanders/bar-ng state
        {
            provide: BarGvkManager,
            useFactory: createBarContainerBridgeFactory<BarGvkManager>(BarGvkManager, container)
        },
        {
            provide: BaasI18n,
            useFactory: createBarContainerBridgeFactory<BaasI18n>(BaasI18n, container)
        },
        {
            provide: BaasBootstrapOptions,
            useFactory: createBarContainerBridgeFactory<BaasBootstrapOptions>(BaasBootstrapOptions, container)
        },
        _BarRouteLogger,
        {
            // NOTE Angular doesn't like symbols for ioc, can lead to strange errors in CDN modus
            provide: BAAS_COMMON_API_CLIENT,
            useFactory: createBarContainerBridgeFactory<BaasCommonApiClient>(BAAS_TYPES.BaasCommonApiClient, container)
        },
        {
            provide: _BaasStaticConfigurationApiClient,
            useFactory: createBarContainerBridgeFactory<_BaasStaticConfigurationApiClient>(_BaasStaticConfigurationApiClient, container)
        },
        {
            provide: _OopsDomService,
            useFactory: createBarContainerBridgeFactory<_OopsDomService>(_OopsDomService, container)
        },
        {
            provide: _BaasTransportHelper,
            useFactory: createBarContainerBridgeFactory<_OopsDomService>(_BaasTransportHelper, container)
        },
        {
            provide: BAR_LOCALHOST,
            useFactory: (options: BaasBootstrapOptions) => options.localhost,
            deps: [BaasBootstrapOptions]
        },
        // NOTE this makes the link with non angular error delegates (e.g. gismap)
        provideBaasErrorHandler(BarErrorDelegateHandlerHook),
        ...defineApmProviders(container)
    ];
}

function defineApmProviders(container: Container) {
    return [
        {
            provide: BAAS_TELEMETRY,
            useFactory: createBarContainerBridgeFactory<BaasTelemetry>(BAAS_TYPES.BaasTelemetry, container)
        },
        {
            provide: _BaasApm,
            useFactory: createBarContainerBridgeFactory<_BaasApm>(_BaasApm, container)
        },
        _BaasApmService,
        provideBaasInitialized(BaasApmInitHook)
    ] as const;
}
